/**
 * Générateur — Attendu 947 — Savoir mettre en relation une représentation en
 * perspective cavalière et un patron d'un pavé, d'un prisme droit ou d'un
 * cylindre de révolution.
 * Niveau : 5ème | Domaine : Espace et géométrie / Solides et représentations planes
 *
 * Plan de cartes (version alignée sur la spécification 947 du 2026-09-21) :
 *
 *   Carte « Patrons d'un cylindre » — QCM d'images : le patron correct parmi
 *     trois faux (exclusion 4 : un patron faux n'est montré que pour être
 *     identifié comme tel).
 *   Carte « Patrons d'un prisme droit » — même chose pour le prisme.
 *   Carte « Les deux bases d'un prisme droit » — QCM : comment on reconnaît
 *     les deux bases, et pourquoi le critère tombe pour le pavé.
 *   Carte « Reporter une longueur sur le patron d'un pavé » — perspective cotée
 *     et patron (ou schéma) côte à côte ; une à trois longueurs à retrouver sur
 *     le patron, à une distance de 0 à 3 arêtes des cotes données.
 *   Carte « Reporter les mesures (prisme) » — schéma du patron d'un prisme droit
 *     à base triangulaire, saisie des largeurs et de la hauteur.
 *   Carte « Reporter les mesures (cylindre) » — du solide vers le patron
 *     (saisie) et du patron vers le solide (QCM).
 *   Carte « Associer solide et patron » — mélange des trois solides.
 *
 * ── Spécification 947 ──────────────────────────────────────────────────────
 *
 * Bornes appliquées :
 *   solides_concernes   liste fermée : pavé, prisme droit, cylindre de révolution
 *   hauteur_du_pave     2 .. 5   largeur_du_pave 2 .. 5   longueur_du_pave 3 .. 10
 *                       (longueur > largeur, les trois deux à deux distinctes)
 *   rayon_de_la_base_du_cylindre 2 .. 7 ; hauteur_du_cylindre 3 .. 10, jamais
 *                       égale au rayon ni au diamètre
 *   nombre_de_cotes_de_la_base_du_prisme 3 .. 6 ; hauteur_du_prisme 2 .. 5
 *   nombre_de_longueurs_a_reporter 1 .. 3
 *   distance_en_aretes_entre_la_cote_donnee_et_la_cote_cherchee 0 .. 3
 *   precision_de_la_reponse_attendue 0,1 cm (annoncée dans la consigne)
 *
 * Exclusions appliquées : aucun cube dans les tâches de report (les trois
 * dimensions du pavé sont deux à deux distinctes) ; deux cotes d'une même
 * perspective ne portent jamais le même nombre ; aucun patron à quatre faces
 * alignées au niveau 2 ; aucun patron faux comme support de report ; aucune
 * construction de patron demandée ; la formule du périmètre du cercle est
 * toujours rappelée dans la consigne.
 *
 * Erreurs visées → mots-clés :
 *   Mesurer ou estimer sur le dessin au lieu de raisonner  → mesure_sur_le_dessin
 *   Ne reporter que d'une arête à sa voisine immédiate      → report_arete_voisine
 *   Chercher les bases par leur position, non par leur forme → bases_par_position
 *   Reporter le rayon ou le diamètre là où il faut le périmètre → rayon_pour_perimetre
 *   Diviser par 2 au lieu de pi, confondre la largeur et le rayon → division_par_deux
 *   Associer une grandeur à une position fixe du dessin     → grandeur_a_position_fixe
 *   Ne pas reconnaître un pavé droit comme un prisme droit  → pave_non_prisme
 *
 * Graduation → cartes :
 *   niveau 1 (pavé en croix ou en T, arête voisine)  reportPave_* (régime n1)
 *   niveau 2 (pavé moins canonique, arête éloignée)  reportPave_* (régime n2)
 *   niveau 3 (prisme droit)                          reportPrisme_*, bases_qcm_unique
 *   niveau 4 (cylindre, du solide vers le patron)    reportCyl_texte_trou_saisie
 *   niveau 5 (cylindre, du patron vers le solide)    reportCyl_qcm_unique
 *   niveau 6 (mélange)                               associer_qcm_unique
 *   réglage transverse : patron aux vraies dimensions, ou schéma non proportionné
 *   réglage transverse : de une à trois longueurs par question
 *
 * TOUT PAR CONSTRUCTION : le patron du pavé est obtenu en DÉPLIANT le solide le
 * long d'un arbre couvrant de son graphe de faces (jamais un dessin figé), le
 * chevauchement est testé à la génération (vigilance 7 : « tester le pliage
 * plutôt que la forme »), et deux arêtes correspondantes sont, par définition,
 * les deux segments du bord qui portent la même arête du solide.
 */

import {
    randInt, randomChoice, shuffle, makeQcm, makeTexteTrou, tex,
} from '../../../utils';
import {
    paveNomme, prismeTri, cylindreDebout,
    patronCylindre, patronPrismeTri, patronPave,
} from './svgSolides';

const ATTENDU_ID = 947;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PCYL = 'Patrons d’un cylindre';
const L_PPRISME = 'Patrons d’un prisme droit';
const L_BASES = 'Les deux bases d’un prisme droit';
const L_PAVE = 'Reporter une longueur sur le patron d’un pavé';
const L_RPRISME = 'Reporter les mesures (prisme)';
const L_RCYL = 'Reporter les mesures (cylindre)';
const L_ASSOC = 'Associer solide et patron';

const salt = () => randInt(0, 999999);
const fr = (x) => String(x).replace('.', ',');
/** Arrondi au dixième à partir de la valeur EXACTE (vigilance 3). */
const dixieme = (x) => Math.round(x * 10) / 10;
const imgChoix = (svg) => ({ type: 'image', config: { imageData: `data:image/svg+xml,${encodeURIComponent(svg)}` } });
/** Figure d'ÉNONCÉ : imageUrl (FORMATS_TANOED §12 ; imageData y fait rejeter le POST). */
const figEnonce = (svg, maxWidth = 'small', alt = 'Figure') => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth, alt } }],
});
const figDouble = (svgSolide, svgPatron, altS, altP) => ({
    version: 1,
    elements: [
        { type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svgSolide)}`, maxWidth: 'small', alt: altS } },
        { type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svgPatron)}`, maxWidth: 'medium', alt: altP } },
    ],
});

// ═══════════════════════════════════════════════════════════
// Dépliage d'un pavé : patron obtenu par un arbre couvrant
// ═══════════════════════════════════════════════════════════

// Axes : 0 = longueur, 1 = largeur, 2 = hauteur. Une face est décrite par sa
// normale sortante `n` et le repère (u, v) de son dessin, avec u × v = n.
const neg = (a) => [-a[0], -a[1], -a[2]];
const cle = (a) => a.join(',');
const axeDe = (a) => a.findIndex((c) => c !== 0);
const D = (a, dims) => dims[axeDe(a)];

/** Face fille obtenue en dépliant par le côté `d` (0 = +u, 1 = -u, 2 = +v, 3 = -v). */
const fille = (f, d, dims) => {
    if (d === 0) return { n: f.u, u: neg(f.n), v: f.v, x: f.x + f.w, y: f.y, w: D(f.n, dims), h: f.h };
    if (d === 1) { const w = D(f.n, dims); return { n: neg(f.u), u: f.n, v: f.v, x: f.x - w, y: f.y, w, h: f.h }; }
    if (d === 2) return { n: f.v, u: f.u, v: neg(f.n), x: f.x, y: f.y + f.h, w: f.w, h: D(f.n, dims) };
    const h = D(f.n, dims); return { n: neg(f.v), u: f.u, v: f.n, x: f.x, y: f.y - h, w: f.w, h };
};
const COTE_OPPOSE = [1, 0, 3, 2];
const chevauche = (a, b) => a.x < b.x + b.w - 1e-9 && b.x < a.x + a.w - 1e-9
    && a.y < b.y + b.h - 1e-9 && b.y < a.y + a.h - 1e-9;

/**
 * Déplie le pavé de dimensions `dims` (longueur, largeur, hauteur) le long d'un
 * arbre couvrant tiré au sort : six faces sans chevauchement, cinq plis.
 * Retourne aussi la même disposition en cases unitaires (schéma non proportionné).
 */
const deplierPave = (dims) => {
    for (let essai = 0; essai < 60; essai++) {
        const racine = { n: [0, 0, 1], u: [1, 0, 0], v: [0, 1, 0], parent: -1, cote: -1 };
        const unites = [{ ...racine, x: 0, y: 0, w: 1, h: 1 }];
        const arbre = [{ parent: -1, cote: -1 }];
        const prises = new Set([cle(racine.n)]);
        let coups = 0;
        while (unites.length < 6 && coups < 300) {
            coups++;
            const i = randInt(0, unites.length - 1);
            const d = randInt(0, 3);
            const f = unites[i];
            const m = [f.u, neg(f.u), f.v, neg(f.v)][d];
            if (prises.has(cle(m))) continue;
            const c = fille(f, d, [1, 1, 1]);
            if (unites.some((g) => chevauche(g, c))) continue;
            prises.add(cle(m));
            unites.push(c);
            arbre.push({ parent: i, cote: d });
        }
        if (unites.length < 6) continue;
        // même arbre, dimensions réelles
        const reels = [{ ...racine, x: 0, y: 0, w: D(racine.u, dims), h: D(racine.v, dims) }];
        for (let i = 1; i < 6; i++) reels.push(fille(reels[arbre[i].parent], arbre[i].cote, dims));
        if (reels.some((a, i) => reels.some((b, j) => j > i && chevauche(a, b)))) continue;
        return { arbre, unites, reels };
    }
    // repli déterministe : le patron en croix
    const arbre = [{ parent: -1, cote: -1 }, { parent: 0, cote: 0 }, { parent: 0, cote: 1 },
        { parent: 0, cote: 2 }, { parent: 0, cote: 3 }, { parent: 3, cote: 2 }];
    const poser = (d) => {
        const racine = { n: [0, 0, 1], u: [1, 0, 0], v: [0, 1, 0] };
        const out = [{ ...racine, x: 0, y: 0, w: D(racine.u, d), h: D(racine.v, d) }];
        for (let i = 1; i < 6; i++) out.push(fille(out[arbre[i].parent], arbre[i].cote, d));
        return out;
    };
    return { arbre, unites: poser([1, 1, 1]), reels: poser(dims) };
};

/** Distance dans l'arbre du patron entre deux faces (nombre de plis traversés). */
const distanceFaces = (arbre, i, j) => {
    const chemin = (k) => { const c = []; let x = k; while (x !== -1) { c.push(x); x = arbre[x].parent; } return c; };
    const ci = chemin(i), cj = chemin(j);
    for (let a = 0; a < ci.length; a++) {
        const b = cj.indexOf(ci[a]);
        if (b !== -1) return a + b;
    }
    return 99;
};

/**
 * Segments du BORD du patron : ceux qui ne sont pas des plis. Chaque arête du
 * solide non pliée y apparaît DEUX fois : ce sont les « arêtes correspondantes »
 * (objets_geometriques 6), celles qui se rejoignent au pliage.
 */
const segmentsDuBord = (faces, arbre) => {
    const plis = new Set();
    for (let i = 1; i < 6; i++) {
        plis.add(`${arbre[i].parent}:${arbre[i].cote}`);
        plis.add(`${i}:${COTE_OPPOSE[arbre[i].cote]}`);
    }
    const segs = [];
    faces.forEach((f, i) => {
        for (let d = 0; d < 4; d++) {
            if (plis.has(`${i}:${d}`)) continue;
            const m = [f.u, neg(f.u), f.v, neg(f.v)][d];
            const bouts = d === 0 ? [[f.x + f.w, f.y], [f.x + f.w, f.y + f.h]]
                : d === 1 ? [[f.x, f.y], [f.x, f.y + f.h]]
                    : d === 2 ? [[f.x, f.y + f.h], [f.x + f.w, f.y + f.h]]
                        : [[f.x, f.y], [f.x + f.w, f.y]];
            segs.push({
                face: i, cote: d,
                arete: [cle(f.n), cle(m)].sort().join('/'),
                axe: axeDe(d < 2 ? f.v : f.u),
                bouts,
                centre: [f.x + f.w / 2, f.y + f.h / 2],
            });
        }
    });
    return { segs, plis };
};

const R2 = (v) => Number(v).toFixed(2);

/** Écartements essayés pour poser une cote du patron, du plus serré au plus large. */
const ECARTS_COTE = [16, 22, 28];
// De part et d'autre de l'arete : le dedans d'une face est vide, donc lisible.
const BIAIS_COTE = [0, -0.5, 0.5];

/** Distance d'un point P au segment [S, U] — sert a eloigner les cotes des traits. */
const distanceSegment = (P, S, U) => {
    const vx = U.x - S.x, vy = U.y - S.y;
    const L2 = vx * vx + vy * vy;
    const t = L2 ? Math.max(0, Math.min(1, ((P.x - S.x) * vx + (P.y - S.y) * vy) / L2)) : 0;
    return Math.hypot(P.x - (S.x + t * vx), P.y - (S.y + t * vy));
};

/** Dessin du patron (ou du schéma) : faces, plis en pointillés, bord plein, cotes. */
const svgPatron = (faces, arbre, marques, vb) => {
    const { segs, plis } = segmentsDuBord(faces, arbre);
    const minX = Math.min(...faces.map((f) => f.x)), maxX = Math.max(...faces.map((f) => f.x + f.w));
    const minY = Math.min(...faces.map((f) => f.y)), maxY = Math.max(...faces.map((f) => f.y + f.h));
    // La marge doit loger les COTES du patron, posées 16 px en dehors de la face et
    // centrées là, en fonte 13 : « 10 cm » y occupe une trentaine de pixels. Les 26 px
    // d'origine laissaient le libellé sortir du cadre (retour prof 2026-09-26).
    const textesCotes = (marques || []).map((mq) => String(mq.texte || ''));
    const pad = textesCotes.length
        ? Math.ceil(ECARTS_COTE[ECARTS_COTE.length - 1] + 8 + (Math.max(...textesCotes.map((t) => t.length)) * 13 * 0.62) / 2)
        : 26;
    const s = Math.min((vb.w - 2 * pad) / (maxX - minX), (vb.h - 2 * pad) / (maxY - minY));
    const offX = (vb.w - s * (maxX - minX)) / 2, offY = (vb.h - s * (maxY - minY)) / 2;
    // y écran vers le bas : on retourne le patron pour que « en haut » reste en haut
    const px = (x) => offX + (x - minX) * s;
    const py = (y) => offY + (maxY - y) * s;
    const out = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb.w} ${vb.h}" width="${vb.w}" height="${vb.h}" font-family="Arial">`];
    out.push(`<rect x="0" y="0" width="${vb.w}" height="${vb.h}" fill="white"/>`);
    faces.forEach((f) => out.push(
        `<rect x="${R2(px(f.x))}" y="${R2(py(f.y + f.h))}" width="${R2(f.w * s)}" height="${R2(f.h * s)}" fill="#eff6ff" stroke="none"/>`,
    ));
    // plis en pointillés
    faces.forEach((f, i) => {
        for (let d = 0; d < 4; d++) {
            if (!plis.has(`${i}:${d}`) || d === 1 || d === 3) continue;   // un pli dessiné une seule fois
            const bouts = d === 0 ? [[f.x + f.w, f.y], [f.x + f.w, f.y + f.h]] : [[f.x, f.y + f.h], [f.x + f.w, f.y + f.h]];
            out.push(`<line x1="${R2(px(bouts[0][0]))}" y1="${R2(py(bouts[0][1]))}" x2="${R2(px(bouts[1][0]))}" y2="${R2(py(bouts[1][1]))}" stroke="#94a3b8" stroke-width="1.2" stroke-dasharray="5 4"/>`);
        }
    });
    segs.forEach((sg) => out.push(
        `<line x1="${R2(px(sg.bouts[0][0]))}" y1="${R2(py(sg.bouts[0][1]))}" x2="${R2(px(sg.bouts[1][0]))}" y2="${R2(py(sg.bouts[1][1]))}" stroke="#1f2937" stroke-width="1.8" stroke-linecap="round"/>`,
    ));
    // Tous les traits du patron, en coordonnées écran : bord plein ET plis pointillés.
    // C'est eux que les étiquettes de cote doivent éviter.
    const traits = [];
    segs.forEach((g) => traits.push([
        { x: px(g.bouts[0][0]), y: py(g.bouts[0][1]) },
        { x: px(g.bouts[1][0]), y: py(g.bouts[1][1]) },
    ]));
    faces.forEach((f, i) => {
        [0, 2].forEach((d) => {
            if (!plis.has(`${i}:${d}`)) return;
            const b = d === 0 ? [[f.x + f.w, f.y], [f.x + f.w, f.y + f.h]] : [[f.x, f.y + f.h], [f.x + f.w, f.y + f.h]];
            traits.push([{ x: px(b[0][0]), y: py(b[0][1]) }, { x: px(b[1][0]), y: py(b[1][1]) }]);
        });
    });
    // cotes : le segment est repassé en couleur, l'étiquette est posée à l'extérieur
    marques.forEach((mq) => {
        const sg = mq.seg;
        const [a, b] = sg.bouts;
        const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
        const dx = mx - sg.centre[0], dy = my - sg.centre[1];
        const n = Math.hypot(dx, dy) || 1;
        // La mesure part vers l'extérieur de SA face — mais l'extérieur d'une face est
        // souvent l'intérieur de la voisine, et le libellé se retrouvait sur une arête
        // (retour prof 2026-09-26). On essaie plusieurs écartements et on garde celui
        // qui éloigne le plus le texte de TOUS les traits du patron, en sondant ses deux
        // bouts et non son seul centre.
        const demi = (String(mq.texte).length * 13 * 0.62) / 2;
        // Direction « vers l'exterieur de la face », en coordonnees ecran.
        const ux = dx / n, uy = -dy / n;
        let pose = null;
        [1, -1].forEach((sens) => BIAIS_COTE.forEach((biais) => ECARTS_COTE.forEach((ec) => {
            const c = Math.cos(biais), si = Math.sin(biais);
            const vx = (ux * c - uy * si) * sens * ec, vy = (ux * si + uy * c) * sens * ec;
            const C = { x: px(mx) + vx, y: py(my) + vy };
            const sondes = [C, { x: C.x - demi, y: C.y }, { x: C.x + demi, y: C.y }];
            const d = Math.min(...sondes.flatMap((S) => traits.map((g) => distanceSegment(S, g[0], g[1]))));
            if (!pose || d > pose.d + 0.01) pose = { d, C };
        })));
        const ex = pose.C.x, ey = pose.C.y;
        const coul = mq.cherchee ? '#c2410c' : '#1d4ed8';
        out.push(`<line x1="${R2(px(a[0]))}" y1="${R2(py(a[1]))}" x2="${R2(px(b[0]))}" y2="${R2(py(b[1]))}" stroke="${coul}" stroke-width="3.4" stroke-linecap="round"/>`);
        out.push(`<text x="${R2(ex)}" y="${R2(ey + 4)}" text-anchor="middle" font-size="13" font-weight="700" fill="${coul}">${mq.texte}</text>`);
    });
    out.push('</svg>');
    return out.join('');
};

// ═══════════════════════════════════════════════════════════
// Tirages (bornes de la spec)
// ═══════════════════════════════════════════════════════════

/** Pavé : longueur > largeur, trois dimensions deux à deux distinctes (exclusion 1). */
const tirerPave = () => {
    for (let essai = 0; essai < 80; essai++) {
        const L = randInt(3, 10), la = randInt(2, 5), h = randInt(2, 5);
        if (L > la && L !== h && la !== h) return [L, la, h];
    }
    return [7, 4, 2];
};

/** Cylindre : rayon et hauteur entiers, jamais deux cotes égales (exclusion 2). */
const tirerCylindre = () => {
    for (let essai = 0; essai < 80; essai++) {
        const r = randInt(2, 7), h = randInt(3, 10);
        if (h !== r && h !== 2 * r) return { r, h };
    }
    return { r: 3, h: 10 };
};

const triangleValide = (a, b, c) => a + b > c && a + c > b && b + c > a;

/** Prisme droit à base triangulaire : côtés distincts, hauteur 2 à 5. */
const tirerPrisme = () => {
    for (let essai = 0; essai < 120; essai++) {
        const a = randInt(3, 6), b = randInt(3, 6), c = randInt(4, 8), p = randInt(2, 5);
        if (triangleValide(a, b, c) && new Set([a, b, c, p]).size === 4) return { a, b, c, p };
    }
    return { a: 3, b: 6, c: 7, p: 4 };
};

// ═══════════════════════════════════════════════════════════
// Carte — Patrons d'un cylindre / d'un prisme droit (QCM d'images)
// ═══════════════════════════════════════════════════════════

const genPatronsCyl = () => {
    // Pas de `vb` impose : patronCylindre cadre lui-meme, a l'echelle commune de la
    // famille. Le cadre de 190x150 coupait les deux disques (retour prof 2026-09-26).
    const bon = imgChoix(patronCylindre('correct', { salt: salt() }).svg);
    const fauxCodes = [
        { v: 'memecote', code: 'bases_par_position' },
        { v: 'rayonsineg', code: 'bases_par_position' },
        { v: 'bouts', code: 'rayon_pour_perimetre' },
    ];
    const faux = shuffle(fauxCodes).map((f) => imgChoix(patronCylindre(f.v, { salt: salt() }).svg));
    const choix = shuffle([bon, ...faux]);
    const texte = '<p>Le patron d\'un cylindre de révolution est formé d\'un rectangle et de deux disques. '
        + '<b>Coche le patron correct.</b></p>'
        + '<p><i>Les deux disques ont le même rayon et sont accolés à deux côtés opposés du rectangle.</i></p>';
    const payload = makeQcm(choix, [bon], texte, opts(L_PCYL), true);
    payload._meta.params = {
        niveau: 'n4', solide: 'cylindre',
        distracteurs: fauxCodes.map((f) => ({ code: f.code, valeur: f.v })),
    };
    return payload;
};

const genPatronsPrisme = () => {
    const bon = imgChoix(patronPrismeTri('correct', { salt: salt() }).svg);
    const fauxCodes = [
        { v: 'basesmemecote', code: 'bases_par_position' },
        { v: 'sansbase', code: 'bases_par_position' },
        { v: 'quatrefaces', code: 'pave_non_prisme' },
    ];
    const faux = shuffle(fauxCodes).map((f) => imgChoix(patronPrismeTri(f.v, { salt: salt() }).svg));
    const choix = shuffle([bon, ...faux]);
    const texte = '<p>On veut le patron d\'un <b>prisme droit à base triangulaire</b> '
        + '(3 faces latérales rectangulaires et 2 triangles superposables). '
        + '<b>Coche le patron correct.</b></p>';
    const payload = makeQcm(choix, [bon], texte, opts(L_PPRISME), true);
    payload._meta.params = {
        niveau: 'n3', solide: 'prisme',
        distracteurs: fauxCodes.map((f) => ({ code: f.code, valeur: f.v })),
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte — Les deux bases d'un prisme droit
// ═══════════════════════════════════════════════════════════

// Groupes nominaux accordés : « triangle équilatéral », jamais « triangle régulier ».
const BASES_PRISME = [
    { n: 3, nom: 'triangle équilatéral', pluriel: 'triangles équilatéraux' },
    { n: 4, nom: 'carré', pluriel: 'carrés' },
    { n: 5, nom: 'pentagone régulier', pluriel: 'pentagones réguliers' },
    { n: 6, nom: 'hexagone régulier', pluriel: 'hexagones réguliers' },
];

/**
 * Quatre questions sur les bases, toutes paramétrées par la base tirée (3 à 6
 * côtés), le côté et la hauteur : la carte n'est jamais deux fois la même.
 */
const questionsBases = (b, c, p) => [
    {
        question: `Un prisme droit a pour base un <b>${b.nom}</b> de côté <b>${c} cm</b> et pour hauteur <b>${p} cm</b>. Sur son patron, comment reconnaît-on les deux bases ?`,
        correct: `Ce sont les deux ${b.pluriel} superposables ; toutes les autres faces sont des rectangles.`,
        distracteurs: [
            { code: 'bases_par_position', valeur: 'Ce sont la face du haut et la face du bas du dessin.' },
            { code: 'bases_par_position', valeur: 'Ce sont les deux faces situées aux extrémités de la bande des rectangles.' },
            { code: 'mesure_sur_le_dessin', valeur: 'Ce sont les deux faces les plus grandes du dessin.' },
        ],
    },
    {
        question: `Un prisme droit a pour base un <b>${b.nom}</b> de côté <b>${c} cm</b> et pour hauteur <b>${p} cm</b>. Quelle est la hauteur commune aux rectangles de son patron ?`,
        correct: `${p} cm`,
        distracteurs: [
            { code: 'grandeur_a_position_fixe', valeur: `${c} cm` },
            { code: 'bases_par_position', valeur: `${b.n * c} cm` },
            { code: 'mesure_sur_le_dessin', valeur: `${c + p} cm` },
        ],
    },
    {
        question: `Un prisme droit a pour base un <b>${b.nom}</b> de côté <b>${c} cm</b> et pour hauteur <b>${p} cm</b>. Sur son patron, quelle est la longueur totale de la bande des rectangles ?`,
        correct: `${b.n * c} cm`,
        distracteurs: [
            { code: 'grandeur_a_position_fixe', valeur: `${c} cm` },
            { code: 'bases_par_position', valeur: `${b.n * p} cm` },
            { code: 'mesure_sur_le_dessin', valeur: `${b.n * c + 2 * p} cm` },
        ],
    },
    {
        question: `Un pavé droit de ${c} cm sur ${p} cm sur ${c + p} cm est-il un prisme droit ?`,
        correct: "Oui : c'est un prisme droit dont les bases sont des rectangles.",
        distracteurs: [
            { code: 'pave_non_prisme', valeur: "Non : les bases d'un prisme droit ne sont jamais des rectangles." },
            { code: 'pave_non_prisme', valeur: "Non : les bases d'un prisme droit sont toujours des triangles." },
            { code: 'bases_par_position', valeur: 'Oui, mais seulement lorsque son patron est en croix.' },
        ],
    },
];

const genBases = () => {
    // Les quatre propositions doivent rester deux à deux distinctes : n × p peut
    // tomber sur c, et le QCM y perdrait une proposition (nombre non constant).
    let b, c, p, q;
    for (let essai = 0; essai < 60 && !q; essai++) {
        b = randomChoice(BASES_PRISME);
        c = randInt(3, 6); p = randInt(2, 5);
        if (c === p) continue;
        // La base carrée est écartée de la question « comment reconnaît-on les
        // bases » : ses bases SONT des rectangles, c'est le cas du pavé, traité
        // par sa propre question.
        const jeu = questionsBases(b, c, p);
        const cand = randomChoice(b.n === 4 ? jeu.slice(1) : jeu);
        if (new Set([cand.correct, ...cand.distracteurs.map((d) => d.valeur)]).size === 4) q = cand;
    }
    if (!q) { b = BASES_PRISME[3]; c = 5; p = 2; q = questionsBases(b, c, p)[0]; }
    const payload = makeQcm([q.correct, ...q.distracteurs.map((d) => d.valeur)], q.correct,
        `<p><b>${q.question}</b></p>`, opts(L_BASES));
    payload._meta.params = {
        niveau: 'n3', solide: 'prisme', n: b.n, cote: c, hauteur: p,
        distracteurs: q.distracteurs,
        erreursVisees: ['bases_par_position'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte — Reporter une longueur sur le patron d'un pavé
// ═══════════════════════════════════════════════════════════

/** Quatre faces alignées ? (exclusion 3, interdite aux niveaux « moins canoniques ») */
const quatreAlignees = (unites) => {
    const lignes = {}, colonnes = {};
    unites.forEach((f) => {
        lignes[f.y] = (lignes[f.y] || 0) + 1;
        colonnes[f.x] = (colonnes[f.x] || 0) + 1;
    });
    return Object.values(lignes).some((v) => v >= 4) || Object.values(colonnes).some((v) => v >= 4);
};

/** Segments du bord utilisables pour une cote : jamais deux segments confondus au dessin. */
const segmentsCotables = (segs) => {
    const pos = (sg) => sg.bouts.map((b) => b.join(',')).sort().join('|');
    const compte = {};
    segs.forEach((sg) => { compte[pos(sg)] = (compte[pos(sg)] || 0) + 1; });
    return segs.filter((sg) => compte[pos(sg)] === 1);
};

/**
 * Tire une planche « perspective cotée + patron » et choisit les cotes données
 * et les cotes cherchées. `regime` : 'n1' (patron en croix ou en T, cote
 * cherchée à une arête au plus) ou 'n2' (patron moins canonique, cote cherchée
 * à deux arêtes au moins).
 */
const tirerPlanchePave = (regime) => {
    for (let essai = 0; essai < 60; essai++) {
        const dims = tirerPave();
        const dep = deplierPave(dims);
        if (!dep) continue;
        const aligne = quatreAlignees(dep.unites);
        if (regime === 'n1' && !aligne) continue;             // croix ou T
        if (regime === 'n2' && aligne) continue;              // exclusion 3
        const echelle = regime === 'n1' ? randomChoice(['vraie', 'schema']) : 'schema';
        const faces = echelle === 'vraie' ? dep.reels : dep.unites;
        const { segs } = segmentsDuBord(faces, dep.arbre);
        const cotables = segmentsCotables(segs);
        // deux cotes données, sur deux axes différents : le troisième axe se déduit
        const parAxe = [0, 1, 2].map((a) => cotables.filter((sg) => sg.axe === a));
        if (parAxe.some((l) => l.length === 0)) continue;
        const axesDonnes = shuffle([0, 1, 2]).slice(0, 2);
        const donnees = axesDonnes.map((a) => randomChoice(parAxe[a]));
        const dmax = regime === 'n1' ? 1 : 3;
        const dmin = regime === 'n1' ? 0 : 2;
        const dist = (sg) => Math.max(0, Math.min(...donnees.map((g) => distanceFaces(dep.arbre, sg.face, g.face))) - 1);
        const candidats = cotables.filter((sg) => !donnees.includes(sg) && dist(sg) >= dmin && dist(sg) <= dmax);
        if (!candidats.length) continue;
        const k = regime === 'n1' ? 1 : randInt(2, 3);
        // deux cotes cherchées ne portent jamais la même dimension : sinon la
        // deuxième réponse se recopie de la première sans rien prouver
        const parAxeCherche = shuffle([0, 1, 2]).map((a) => candidats.filter((sg) => sg.axe === a))
            .filter((l) => l.length);
        const cherchees = parAxeCherche.slice(0, k).map((l) => randomChoice(l));
        if (cherchees.length < Math.min(k, 2)) continue;
        return { dims, dep, faces, echelle, donnees, cherchees, regime, aligne, distances: cherchees.map(dist) };
    }
    return null;
};

/** Une planche valable, coûte que coûte : le régime demandé, sinon le niveau 1. */
const planchePave = (regime) => {
    for (let i = 0; i < 6; i++) { const pl = tirerPlanchePave(regime); if (pl) return pl; }
    for (let i = 0; i < 30; i++) { const pl = tirerPlanchePave('n1'); if (pl) return pl; }
    return tirerPlanchePave('n1');
};

const LETTRES_COTE = ['a', 'b', 'c'];

const planchePaveSvg = (pl) => {
    const marques = [
        ...pl.donnees.map((sg) => ({ seg: sg, texte: `${pl.dims[sg.axe]} cm`, cherchee: false })),
        ...pl.cherchees.map((sg, i) => ({ seg: sg, texte: LETTRES_COTE[i], cherchee: true })),
    ];
    return svgPatron(pl.faces, pl.dep.arbre, marques, { w: 300, h: 260 });
};

const perspectivePaveCotee = (dims) => paveNomme(
    { L: dims[0] * 0.5 + 1.6, H: dims[2] * 0.5 + 1.6, P: dims[1] * 0.5 + 1.4 },
    {
        vb: { w: 220, h: 190 }, labels: false, salt: salt(),
        coter: { L: `${dims[0]} cm`, H: `${dims[2]} cm`, P: `${dims[1]} cm` },
    },
).svg;

const enonceEchelle = (pl) => (pl.echelle === 'schema'
    ? '<p><i>Attention : ce dessin est un <b>schéma</b>, il n\'est pas à l\'échelle. '
      + 'Les longueurs ne se mesurent pas sur le dessin, elles se retrouvent en suivant les arêtes.</i></p>'
    : '<p><i>Le patron est dessiné aux vraies dimensions.</i></p>');

const genReportPave = (regime) => () => {
    const pl = planchePave(regime);
    const phrase = pl.cherchees
        .map((sg, i) => `${LETTRES_COTE[i]} : {{${pl.dims[sg.axe]}}} cm`)
        .join('\n');
    const texte = '<p>Voici un pavé droit en perspective cavalière et '
        + `${pl.echelle === 'schema' ? 'un schéma de' : ''} son patron.</p>`
        + `<p><b>Quelle est la longueur de chaque arête marquée en orange ?</b></p>`
        + enonceEchelle(pl);
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_PAVE), mode: 'saisie', strict: false,
        elements_additionnels: figDouble(perspectivePaveCotee(pl.dims), planchePaveSvg(pl),
            'Pavé droit en perspective cavalière, avec ses trois dimensions',
            'Patron du pavé droit'),
    });
    payload._meta.params = {
        niveau: pl.regime, solide: 'pave', dims: pl.dims, echelle: pl.echelle, aligne: pl.aligne,
        nbCherchees: pl.cherchees.length, distances: pl.distances,
        erreursVisees: [
            ...(pl.echelle === 'schema' ? ['mesure_sur_le_dessin'] : []),
            ...(regime === 'n2' ? ['report_arete_voisine'] : []),
            'grandeur_a_position_fixe',
        ],
    };
    return payload;
};

const genReportPaveQcm = () => {
    const pl = planchePave(randomChoice(['n1', 'n2']));
    const cible = pl.cherchees[0];
    const bonne = pl.dims[cible.axe];
    const autres = [0, 1, 2].filter((a) => a !== cible.axe);
    // La somme des deux autres dimensions peut tomber sur la bonne réponse : dans
    // ce cas on prend la somme des trois, toujours strictement plus grande que
    // chacune d'elles. Jamais deux propositions identiques (le QCM en perdrait une).
    const somme = pl.dims[autres[0]] + pl.dims[autres[1]];
    const troisieme = somme === bonne ? pl.dims[0] + pl.dims[1] + pl.dims[2] : somme;
    const distracteurs = [
        { code: 'report_arete_voisine', valeur: `${pl.dims[autres[0]]} cm` },
        { code: 'grandeur_a_position_fixe', valeur: `${pl.dims[autres[1]]} cm` },
        { code: 'mesure_sur_le_dessin', valeur: `${troisieme} cm` },
    ];
    const marques = [
        ...pl.donnees.map((sg) => ({ seg: sg, texte: `${pl.dims[sg.axe]} cm`, cherchee: false })),
        { seg: cible, texte: 'a', cherchee: true },
    ];
    const svg = svgPatron(pl.faces, pl.dep.arbre, marques, { w: 300, h: 260 });
    const texte = `<p>Voici un pavé droit en perspective cavalière et ${pl.echelle === 'schema' ? 'un schéma de' : ''} son patron.</p>`
        + '<p><b>Quelle est la longueur de l\'arête marquée a ?</b></p>'
        + enonceEchelle(pl);
    const payload = makeQcm([`${bonne} cm`, ...distracteurs.map((d) => d.valeur)], `${bonne} cm`, texte, {
        ...opts(L_PAVE),
        elements_additionnels: figDouble(perspectivePaveCotee(pl.dims), svg,
            'Pavé droit en perspective cavalière, avec ses trois dimensions', 'Patron du pavé droit'),
    });
    payload._meta.params = {
        niveau: pl.regime, solide: 'pave', dims: pl.dims, echelle: pl.echelle, aligne: pl.aligne,
        nbCherchees: 1, distances: [pl.distances[0]], distracteurs,
        erreursVisees: pl.echelle === 'schema' ? ['mesure_sur_le_dessin'] : ['grandeur_a_position_fixe'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte — Reporter les mesures (prisme droit)
// ═══════════════════════════════════════════════════════════

const genReportPrisme = () => {
    const { a, b, c, p } = tirerPrisme();
    const svgSolide = prismeTri({ vb: { w: 190, h: 160 }, salt: salt() }).svg;
    const svgP = patronPrismeTri('correct', { salt: salt() }).svg;
    // Jamais une étiquette qui contient sa propre réponse : les deux mesures
    // cherchées se déduisent du solide, pas du libellé.
    const mesures = shuffle([
        { t: `La hauteur commune aux trois rectangles : {{${p}}} cm` },
        { t: `La longueur totale de la bande des trois rectangles : {{${a + b + c}}} cm` },
    ]).slice(0, randInt(1, 2));
    const phrase = mesures.map((m) => m.t).join('\n');
    const texte = `<p>Un prisme droit a pour base un triangle de côtés <b>${a} cm</b>, <b>${b} cm</b> `
        + `et <b>${c} cm</b>, et pour hauteur <b>${p} cm</b>. Voici le prisme et un schéma de son patron : `
        + 'trois rectangles et deux triangles superposables.</p>'
        + '<p><b>Complète les mesures du patron.</b></p>'
        + '<p><i>Attention : ce dessin est un <b>schéma</b>, il n\'est pas à l\'échelle.</i></p>';
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_RPRISME), mode: 'saisie', strict: false,
        elements_additionnels: figDouble(svgSolide, svgP,
            'Prisme droit à base triangulaire en perspective cavalière', 'Schéma du patron du prisme droit'),
    });
    payload._meta.params = {
        niveau: 'n3', solide: 'prisme', a, b, c, p, echelle: 'schema',
        nbCherchees: mesures.length,
        erreursVisees: ['mesure_sur_le_dessin', 'grandeur_a_position_fixe'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte — Reporter les mesures (cylindre), dans les deux sens
// ═══════════════════════════════════════════════════════════

// Le rayon est une lettre de variable : il s'écrit en italique mathématique, jamais en
// lettre ordinaire (règle d'équipe, retour prof 2026-09-24). Le rappel vit dans un
// ÉNONCÉ, où la regex de rendu exige une commande LaTeX en tête du fragment : tex()
// pose le `\mathrm{}` de largeur nulle qui la satisfait.
const RAPPEL_PERIMETRE = `Rappel : le périmètre d'un cercle de rayon ${tex('r')} vaut `
    + `${tex('2 \\times \\pi \\times r')}. `
    + 'Donne les longueurs arrondies au dixième de centimètre.';

const genReportCyl = () => {
    const { r, h } = tirerCylindre();
    const longueur = dixieme(2 * Math.PI * r);          // arrondi de la valeur EXACTE
    const svgSolide = cylindreDebout({
        r: r * 0.35 + 0.9, h: h * 0.35 + 1.2, vb: { w: 170, h: 195 }, salt: salt(),
        coter: { r: `${r} cm`, h: `${h} cm` },
    }).svg;
    const svgP = patronCylindre('correct', { salt: salt() }).svg;
    const mesures = shuffle([
        { t: `Diamètre d'un disque : {{${2 * r}}} cm`, code: 'rayon_pour_perimetre' },
        { t: `Largeur du rectangle : {{${h}}} cm`, code: 'grandeur_a_position_fixe' },
        { t: `Longueur du rectangle : {{${fr(longueur)}}} cm`, code: 'rayon_pour_perimetre' },
    ]).slice(0, randInt(1, 3));
    const phrase = mesures.map((m) => m.t).join('\n');
    const texte = `<p>Un cylindre de révolution a pour <b>rayon ${r} cm</b> et pour <b>hauteur ${h} cm</b>. `
        + 'Voici le cylindre et un schéma de son patron : un rectangle et deux disques.</p>'
        + '<p><b>Complète les mesures du patron.</b></p>'
        + `<p><i>${RAPPEL_PERIMETRE} Ce dessin est un <b>schéma</b>, il n'est pas à l'échelle.</i></p>`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_RCYL), mode: 'saisie', strict: false,
        elements_additionnels: figDouble(svgSolide, svgP,
            'Cylindre de révolution en perspective cavalière, rayon et hauteur cotés',
            'Schéma du patron du cylindre'),
    });
    payload._meta.params = {
        niveau: 'n4', solide: 'cylindre', r, h, echelle: 'schema',
        nbCherchees: mesures.length,
        erreursVisees: ['rayon_pour_perimetre', 'mesure_sur_le_dessin'],
    };
    return payload;
};

/** Du patron vers le solide : la longueur du rectangle est donnée, le rayon est cherché. */
const genReportCylInverse = () => {
    const { r, h } = tirerCylindre();
    const longueur = dixieme(2 * Math.PI * r);
    const svgP = patronCylindre('correct', { salt: salt() }).svg;
    const distracteurs = [
        { code: 'division_par_deux', valeur: `${fr(dixieme(longueur / 2))} cm` },
        { code: 'division_par_deux', valeur: `${h} cm` },
        { code: 'rayon_pour_perimetre', valeur: `${2 * r} cm` },
    ];
    const correct = `${r} cm`;
    const texte = '<p>Le patron d\'un cylindre de révolution est formé d\'un rectangle et de deux disques. '
        + `Le rectangle a pour longueur <b>${fr(longueur)} cm</b> et pour largeur <b>${h} cm</b>.</p>`
        + '<p><b>Quel est le rayon du cercle de base de ce cylindre ?</b></p>'
        + `<p><i>${RAPPEL_PERIMETRE}</i></p>`;
    const payload = makeQcm([correct, ...distracteurs.map((d) => d.valeur)], correct, texte, {
        ...opts(L_RCYL), elements_additionnels: figEnonce(svgP, 'medium', 'Schéma du patron du cylindre'),
    });
    payload._meta.params = {
        niveau: 'n5', solide: 'cylindre', r, h, longueur, distracteurs,
        erreursVisees: ['division_par_deux'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte — Associer un solide à SON patron (mélange)
// ═══════════════════════════════════════════════════════════

const genAssocier = () => {
    const vbS = { w: 150, h: 132 };
    const type = randomChoice(['cylindre', 'prisme', 'pave']);
    let svgSolide, bon;
    if (type === 'cylindre') {
        const { r, h } = tirerCylindre();
        svgSolide = cylindreDebout({ r: r * 0.35 + 0.9, h: h * 0.35 + 1.2, vb: vbS, salt: salt() }).svg;
        bon = patronCylindre('correct', { salt: salt() }).svg;
    } else if (type === 'prisme') {
        svgSolide = prismeTri({ vb: vbS, salt: salt() }).svg;
        bon = patronPrismeTri('correct', { salt: salt() }).svg;
    } else {
        const dims = tirerPave();
        svgSolide = paveNomme({ L: dims[0] * 0.5 + 1.6, H: dims[2] * 0.5 + 1.6, P: dims[1] * 0.5 + 1.4 },
            { vb: vbS, labels: false, salt: salt() }).svg;
        bon = patronPave({ salt: salt() }).svg;
    }
    const autres = {
        cylindre: () => patronCylindre('correct', { salt: salt() }).svg,
        prisme: () => patronPrismeTri('correct', { salt: salt() }).svg,
        pave: () => patronPave({ salt: salt() }).svg,
    };
    const distracteurs = Object.keys(autres).filter((t) => t !== type)
        .map((t) => ({ code: 'pave_non_prisme', valeur: t, svg: autres[t]() }));
    // quatrième proposition : un patron de prisme dont il manque une base
    distracteurs.push({
        code: 'bases_par_position', valeur: 'sansbase',
        svg: patronPrismeTri('sansbase', { salt: salt() }).svg,
    });
    const bonImg = imgChoix(bon);
    const choix = shuffle([bonImg, ...distracteurs.slice(0, 3).map((d) => imgChoix(d.svg))]);
    const texte = '<p>Voici un solide en perspective cavalière. <b>Quel est son patron ?</b></p>';
    const payload = makeQcm(choix, bonImg, texte, {
        ...opts(L_ASSOC), elements_additionnels: figEnonce(svgSolide, 'small', 'Solide en perspective cavalière'),
    });
    payload._meta.params = {
        niveau: 'n6', solide: type,
        distracteurs: distracteurs.slice(0, 3).map((d) => ({ code: d.code, valeur: d.valeur })),
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    patronsCyl_qcm_multiple: genPatronsCyl,
    patronsPrisme_qcm_multiple: genPatronsPrisme,
    bases_qcm_unique: genBases,
    reportPave_texte_trou_saisie: genReportPave('n1'),
    reportPave_complexe_texte_trou_saisie: genReportPave('n2'),
    reportPave_qcm_unique: genReportPaveQcm,
    reportPrisme_texte_trou_saisie: genReportPrisme,
    reportCyl_texte_trou_saisie: genReportCyl,
    reportCyl_qcm_unique: genReportCylInverse,
    associer_qcm_unique: genAssocier,
};

const FORMAT_LABELS = {
    qcm_multiple: 'QCM images', qcm_unique: 'QCM',
    texte_trou_saisie: 'Saisie', complexe_texte_trou_saisie: 'Saisie (patron moins canonique)',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'patronsCyl', label: L_PCYL, formatsList: buildFormats('patronsCyl') },
    { id: 'patronsPrisme', label: L_PPRISME, formatsList: buildFormats('patronsPrisme') },
    { id: 'bases', label: L_BASES, formatsList: buildFormats('bases') },
    { id: 'reportPave', label: L_PAVE, formatsList: buildFormats('reportPave') },
    { id: 'reportPrisme', label: L_RPRISME, formatsList: buildFormats('reportPrisme') },
    { id: 'reportCyl', label: L_RCYL, formatsList: buildFormats('reportCyl') },
    { id: 'associer', label: L_ASSOC, formatsList: buildFormats('associer') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { deplierPave, segmentsDuBord, distanceFaces, planchePave };
